// The submission flow's events (Signup/docs/33, docs/35 S2). GA4 sees page views anyway; these
// add the funnel's steps, chiefly the ones before the email is verified, which nothing else
// records. Conversion attribution still lives in the backend tables (docs/05, docs/11): GA4
// is directional.
//
// What goes to GA4 is two allow lists:
// - events: every step's success under its own name; every negative outcome under one of two
//   generic names (sf_step_failed, sf_step_declined) with the step and the reason or choice;
// - parameters: the flow's context (market, language, account, arm, casting call, src) and the
//   step/reason/choice. Nothing else, whatever a page passes: no names, emails, phones, ids or keys.
// Everything else stays a console line in mock mode, as before, ready to switch on by name.
//
// GA4 itself only loads in production (helpers/ga.ts); elsewhere the events land in
// window.dataLayer and go nowhere.

import { getTrackingData } from "helpers";
import i18n from "../../i18n";
import { gaEvent } from "../../helpers/ga";
import { isMockApi } from "../../shared/api/submission";
import { peekStep4Variant } from "./common/step4Variant";

// Each step's success, as the flow names it (docs/33 §3; the classic upgrade pages, §3 too).
export const SUCCESS_EVENTS = [
  "submission_role_selected",
  "sf_signup_form_viewed",
  "sf_signup_code_sent",
  "sf_account_created",
  "sf_login_returned",
  "sf_materials_saved",
  "sf_phone_verified",
  "sf_phone_skipped_on_plan",
  "sf_step4_review_viewed",
  "sf_step4_to_paywall",
  "sf_step4_to_checkout",
  "sf_step4_checkout_completed",
  "sf_step4_sent",
  "sf_step4_sent_go_pro",
  "sf_continue_viewed",
  "sf_pro_plans_viewed",
  "sf_pro_checkout_viewed",
  "sf_pro_paid",
];

export const FAILED_EVENT = "sf_step_failed";
export const DECLINED_EVENT = "sf_step_declined";

// The flow's own negative events, folded into the two generic ones.
type Negative = { event: string; step: string; reason?: (props: any) => string | undefined; choice?: (props: any) => string | undefined };
export const NEGATIVE_EVENTS: { [name: string]: Negative } = {
  sf_email_code_failed: { event: FAILED_EVENT, step: "email_code", reason: (p) => p.reason },
  sf_login_return_failed: { event: FAILED_EVENT, step: "login_return", reason: () => "handOffFailed" },
  submission_breakdown_closed_shown: { event: FAILED_EVENT, step: "casting_call", reason: () => "breakdown_closed" },
  sf_step4_checkout_failed: { event: FAILED_EVENT, step: "checkout", reason: (p) => p.reason || p.code },
  sf_step4_send_after_payment_failed: { event: FAILED_EVENT, step: "send_after_payment", reason: (p) => p.reason || p.code },
  sf_step4_paywall_declined: { event: DECLINED_EVENT, step: "paywall", choice: () => "declined" },
  sf_step4_back_from_paywall: { event: DECLINED_EVENT, step: "paywall", choice: () => "back" },
  sf_step4_checkout_cancelled: { event: DECLINED_EVENT, step: "checkout", choice: () => "cancelled" },
  sf_materials_deferred: { event: DECLINED_EVENT, step: "materials", choice: (p) => p.source },
  // Casting doesn't take the actor's country (countryClosed.ts): shown, then the exit taken.
  sf_country_not_eligible_shown: { event: FAILED_EVENT, step: "country_gate", reason: () => "country_not_eligible" },
  sf_country_not_eligible_exit: { event: DECLINED_EVENT, step: "country_gate", choice: (p) => p.choice },
};

// The only parameters GA4 ever gets.
export const ALLOWED_PARAMS = ["flow", "market", "lang", "account", "step4_arm", "breakdown_id", "src", "step", "reason", "choice"];

// What the flow knows about the actor so far: set by the model and pages as they learn it.
// Kept in the tab, so a full page load (back from Stripe, a refresh, an email's link) doesn't
// send the next events as "unknown" with no market until the page has asked again (docs/36).
interface AnalyticsContext {
  market?: string;
  newSignup?: boolean;
  onPlan?: boolean;
}
const CONTEXT_KEY = "sfAnalyticsContext.v1";
const COMPLETED_KEY = "sfCheckoutsCompleted.v1";

const readContext = (): AnalyticsContext => {
  try {
    const raw = window.sessionStorage.getItem(CONTEXT_KEY);
    const kept = raw ? JSON.parse(raw) : null;
    return kept && typeof kept === "object" ? kept : {};
  } catch (e) {
    return {};
  }
};

const keepContext = (next: AnalyticsContext) => {
  try {
    if (Object.keys(next).length) window.sessionStorage.setItem(CONTEXT_KEY, JSON.stringify(next));
    else window.sessionStorage.removeItem(CONTEXT_KEY);
  } catch (e) {
    // Without storage, a full page load starts the context again, as before.
  }
};

let context: AnalyticsContext = readContext();

export const setSubmissionAnalyticsContext = (next: AnalyticsContext) => {
  context = { ...context, ...Object.keys(next).reduce((o: any, k) => {
    if ((next as any)[k] !== undefined) o[k] = (next as any)[k];
    return o;
  }, {}) };
  keepContext(context);
};

export const resetSubmissionAnalyticsContext = () => {
  context = {};
  keepContext(context);
  // The previous person's completed checkouts too: the next one's, for the same casting call,
  // must still be counted (trackCheckoutCompleted).
  try {
    window.sessionStorage.removeItem(COMPLETED_KEY);
  } catch (e) {
    // ignore
  }
};

/** Test seam: the context as a new page load would find it in the tab. */
export const reloadSubmissionAnalyticsContextForTests = () => {
  context = readContext();
};

// Moments a different actor may take over the tab: what was learnt about the previous one goes,
// then what the moment itself says (new account, existing account, or nothing yet on log out).
const ACTOR_CHANGES: { [name: string]: boolean | null } = {
  sf_account_created: true,
  sf_login_returned: false,
  sf_classic_session_continue: false,
  sf_log_out: null,
};

const MARKETS = ["ca", "us", "mx"];
const market = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const code = String(value).trim().toLowerCase();
  return MARKETS.indexOf(code) >= 0 ? code : "other";
};

const accountOf = (c: AnalyticsContext): string =>
  c.onPlan ? "pro" : c.newSignup === true ? "new" : c.newSignup === false ? "existing" : "unknown";

const search = (): URLSearchParams => {
  try { return new URLSearchParams(window.location.search); } catch (e) { return new URLSearchParams(""); }
};

const clip = (value: any): string | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  return String(value).slice(0, 100);   // GA4's limit for a parameter value
};

/** The GA4 event for one of the flow's events, or null when it stays local. */
export const toGaEvent = (name: string, props: any = {}): { name: string; params: { [k: string]: string } } | null => {
  if (name in ACTOR_CHANGES) {
    resetSubmissionAnalyticsContext();
    const newSignup = ACTOR_CHANGES[name];
    if (newSignup !== null) setSubmissionAnalyticsContext({ newSignup });
  }

  const negative = NEGATIVE_EVENTS[name];
  const direct = name === FAILED_EVENT || name === DECLINED_EVENT;
  if (!negative && !direct && SUCCESS_EVENTS.indexOf(name) < 0) return null;

  const url = search();
  const tracking: any = (() => { try { return getTrackingData() || {}; } catch (e) { return {}; } })();
  const raw: { [k: string]: any } = {
    flow: "submission",
    market: market(context.market || url.get("ctry")),
    lang: (i18n.language || "").slice(0, 2) || undefined,
    account: accountOf(context),
    step4_arm: props.variant || peekStep4Variant() || undefined,
    breakdown_id: props.breakdownId || url.get("bd") || undefined,
    src: name === "sf_continue_viewed" ? url.get("src") || tracking.queryParamSource : tracking.queryParamSource || url.get("src"),
  };
  if (negative) {
    raw.step = negative.step;
    if (negative.reason) raw.reason = negative.reason(props || {});
    if (negative.choice) raw.choice = negative.choice(props || {});
  } else {
    raw.step = props.step;
    raw.reason = props.reason;
    raw.choice = props.choice;
  }

  const params: { [k: string]: string } = {};
  ALLOWED_PARAMS.forEach((key) => {
    const value = clip(raw[key]);
    if (value !== undefined) params[key] = value;
  });
  return { name: negative ? negative.event : name, params };
};

// A checkout completed, once per casting call for whoever has the tab: the flow's own checkout
// return (tests C and D) and step 4 back from the membership paywall's checkout (tests A and B,
// paid=1) both say so, and step 4 is reached after the first one too, or reloaded.
export const trackCheckoutCompleted = (breakdownId: string | null | undefined, props: any = {}) => {
  const key = breakdownId || "";
  try {
    const raw = window.sessionStorage.getItem(COMPLETED_KEY);
    const seen: string[] = raw ? JSON.parse(raw) : [];
    if (seen.indexOf(key) >= 0) return;
    window.sessionStorage.setItem(COMPLETED_KEY, JSON.stringify(seen.concat(key).slice(-20)));
  } catch (e) {
    // Without storage, the event may come twice for one checkout; never not at all.
  }
  trackSubmissionEvent("sf_step4_checkout_completed", props);
};

export const trackSubmissionEvent = (name: string, props?: any) => {
  try {
    if (isMockApi()) {
      // eslint-disable-next-line no-console
      console.info(`[submission-event] ${name}`, { ...(props || {}), tracking: getTrackingData() });
    }
    const ga = toGaEvent(name, props || {});
    if (ga) gaEvent(ga.name, ga.params);
  } catch (e) {
    // Analytics must never break the flow.
  }
};
